import { useState } from 'react';

import { type HolidaySuggestion, tagLabel } from '@/store/proactive/proactive.mappers';

import { ActionsButton } from '../ActionsButton/ActionsButton';
import { Button, ButtonOutlined } from '../Button';
import ContextMenu from '../ContextMenu/ContextMenu';
import Modal from '../Modal/Modal';
import { StatusPill } from '../StatusPill/StatusPill';

import s from './HolidaySuggestionModal.module.scss';
import { reminderOptions } from './reminderOptions';

type HolidaySuggestionModalProps = {
  suggestion: HolidaySuggestion | null;
  onClose: () => void;
  onPlan: () => void;
  /** Remind at the moment the person picked. */
  onRemindLater: (until: Date) => void;
  /** "Don't suggest <holiday> again" (topic) or "Turn off holiday ideas" (kind). */
  onMute: (scope: 'topic' | 'kind') => void;
  /** A reaction is in flight — the buttons wait for it. */
  busy?: boolean;
};

const formatHolidayDate = (date: Date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

/**
 * The AI's idea for an upcoming holiday: what to do, why now, and the answers —
 * Plan it (opens the event wizard) and Remind me later (pick when); muting lives
 * in the ⋯ menu, ✕ just closes. Laid out like the app's other notice modals
 * (TaskNotifier): secondary then primary, aligned right. Purely presentational;
 * HolidaySuggestionMount owns when it shows and what the answers do.
 *
 * @alias ai idea, holiday idea, proactive suggestion, suggestion card
 */
export const HolidaySuggestionModal = ({
  suggestion,
  onClose,
  onPlan,
  onRemindLater,
  onMute,
  busy = false,
}: HolidaySuggestionModalProps) => {
  // The menu's anchor lives in state (not a ref read during render).
  const [remindAnchor, setRemindAnchor] = useState<HTMLElement | null>(null);
  const remindOpen = Boolean(remindAnchor);
  if (!suggestion) return null;
  const { holiday } = suggestion;
  const reminders = reminderOptions(suggestion);

  return (
    <Modal open onClose={onClose} title="AI Idea">
      <div className={s.body}>
        <p className={s.eyebrow}>
          <span className={s.mark} aria-hidden="true">
            ✦
          </span>
          {holiday.name} · {formatHolidayDate(holiday.date)}
        </p>

        <h3 className={s.title}>{suggestion.title}</h3>
        {suggestion.description && <p className={s.description}>{suggestion.description}</p>}

        {suggestion.tags.length > 0 && (
          <ul className={s.tags} aria-label="Tags">
            {suggestion.tags.map((tag) => (
              <li key={tag}>
                <StatusPill label={tagLabel(tag)} tone="info" />
              </li>
            ))}
          </ul>
        )}

        {suggestion.reason && <p className={s.reason}>{suggestion.reason}</p>}

        <div className={s.actions}>
          <ActionsButton
            ariaLabel="More Options"
            className={s.more}
            items={[
              { label: `Don’t Suggest ${holiday.name} Again`, onClick: () => onMute('topic') },
              { label: 'Turn Off Holiday Ideas', onClick: () => onMute('kind') },
            ]}
          />
          {reminders.length > 0 && (
            <ButtonOutlined
              type="button"
              onClick={(event) => {
                const target = event.currentTarget;
                setRemindAnchor((prev) => (prev ? null : target));
              }}
              disabled={busy}
              aria-haspopup="menu"
              aria-expanded={remindOpen}
            >
              Remind Me Later
            </ButtonOutlined>
          )}
          <Button type="button" onClick={onPlan} disabled={busy}>
            Plan Event
          </Button>
        </div>
        <ContextMenu
          isOpen={remindOpen}
          onClose={() => setRemindAnchor(null)}
          anchorEl={remindAnchor}
          items={reminders.map(({ label, until }) => ({
            label,
            onClick: () => {
              setRemindAnchor(null);
              onRemindLater(until);
            },
          }))}
        />
      </div>
    </Modal>
  );
};

export default HolidaySuggestionModal;
